import { useState, type ReactNode } from 'react'
import {
  AppBar, Avatar, Box, Divider, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Toolbar,
  Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import {
  AlertTriangle, BadgeCheck, BarChart3, Building2, CalendarCheck, CalendarDays, CircleCheckBig, Hammer,
  ClipboardCheck, CopyCheck, FileText, FlaskConical, Gauge, Globe2, GraduationCap, Languages,
  History, IdCard, LogOut, Menu as MenuIcon, MessageCircle, Package, Receipt, ShoppingCart,
  CalendarOff, HandCoins, ReceiptText, Timer, Target, Truck, Wallet,
  ScrollText, Upload, Users, UserSquare2,
} from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  hasEntity, hasModule,
  type AppEntity, type AppModule, type Language, type Permission,
} from '@mefosa/shared'
import { useAuth } from '../../auth/AuthProvider'
import { BRAND } from '../../theme'

const DRAWER_WIDTH = 248

interface NavItem {
  to: string
  labelKey: string
  icon: ReactNode
  /**
   * The screen this row IS. Both checks are needed, not one or the other.
   *
   * Permission alone showed a row the person was never granted: Non-conformities
   * needs audit.read for its required picker, so a permission check let the
   * Audits row through with it.
   *
   * Entity alone showed a row the person's ROLE cannot use: every unrestricted
   * account holds every screen, so a sales user saw a Reports link that led
   * straight to a refusal.
   */
  entity: AppEntity
  /** The minimum this row's screen needs. Absent when the role never gates it. */
  permission?: Permission
}

interface NavSection {
  titleKey: string
  /**
   * The section this belongs to, so it can be hidden wholesale.
   *
   * Needed on top of the per-item permission because some items carry no
   * permission at all — the dashboard among them — and would otherwise leak
   * out of a section the person was never given.
   */
  module: AppModule
  items: NavItem[]
}

const SECTIONS: NavSection[] = [
  {
    titleKey: 'nav.sectionOverview',
    module: 'overview',
    items: [
      { to: '/dashboard', labelKey: 'nav.dashboard', icon: <Gauge size={20} />, entity: 'dashboard' },
      { to: '/today', labelKey: 'nav.today', icon: <CalendarCheck size={20} />, entity: 'today', permission: 'followUp.read' },
      { to: '/activity', labelKey: 'nav.activityLog', icon: <History size={20} />, entity: 'activity-log', permission: 'followUp.read' },
    ],
  },
  {
    titleKey: 'nav.sectionPipeline',
    module: 'pipeline',
    items: [
      { to: '/companies', labelKey: 'nav.companies', icon: <Building2 size={20} />, entity: 'companies', permission: 'company.read' },
      { to: '/contacts', labelKey: 'nav.contacts', icon: <UserSquare2 size={20} />, entity: 'contacts', permission: 'contact.read' },
      { to: '/whatsapp', labelKey: 'nav.whatsapp', icon: <MessageCircle size={20} />, entity: 'whatsapp', permission: 'followUp.read' },
      { to: '/deals/follow-ups', labelKey: 'nav.followUps', icon: <Target size={20} />, entity: 'deals', permission: 'deal.read' },
      { to: '/deals/implementation', labelKey: 'nav.implementation', icon: <Hammer size={20} />, entity: 'deals', permission: 'deal.read' },
      { to: '/deals/closed', labelKey: 'nav.closed', icon: <CircleCheckBig size={20} />, entity: 'deals', permission: 'deal.read' },
      { to: '/import', labelKey: 'nav.import', icon: <Upload size={20} />, entity: 'import', permission: 'import.run' },
      { to: '/duplicates', labelKey: 'nav.duplicates', icon: <CopyCheck size={20} />, entity: 'duplicates', permission: 'company.delete' },
    ],
  },
  {
    titleKey: 'nav.sectionCompliance',
    module: 'compliance',
    items: [
      { to: '/audits', labelKey: 'nav.audits', icon: <ClipboardCheck size={20} />, entity: 'audits', permission: 'audit.read' },
      { to: '/findings', labelKey: 'nav.findings', icon: <AlertTriangle size={20} />, entity: 'findings', permission: 'finding.read' },
      { to: '/certificates', labelKey: 'nav.certificates', icon: <BadgeCheck size={20} />, entity: 'certificates', permission: 'certificate.read' },
    ],
  },
  {
    titleKey: 'nav.sectionAcademy',
    module: 'academy',
    items: [
      { to: '/courses', labelKey: 'nav.courses', icon: <GraduationCap size={20} />, entity: 'courses', permission: 'course.read' },
      { to: '/sessions', labelKey: 'nav.sessions', icon: <CalendarDays size={20} />, entity: 'sessions', permission: 'session.read' },
      { to: '/trainees', labelKey: 'nav.trainees', icon: <Users size={20} />, entity: 'trainees', permission: 'trainee.read' },
    ],
  },
  {
    titleKey: 'nav.sectionOperations',
    module: 'operations',
    items: [
      { to: '/lab', labelKey: 'nav.lab', icon: <FlaskConical size={20} />, entity: 'laboratory', permission: 'lab.read' },
      { to: '/products', labelKey: 'nav.products', icon: <Package size={20} />, entity: 'inventory', permission: 'product.read' },
      { to: '/purchases', labelKey: 'nav.purchases', icon: <ShoppingCart size={20} />, entity: 'purchases', permission: 'purchase.read' },
      { to: '/suppliers', labelKey: 'nav.suppliers', icon: <Truck size={20} />, entity: 'suppliers', permission: 'product.read' },
      { to: '/projects', labelKey: 'nav.projects', icon: <Globe2 size={20} />, entity: 'projects', permission: 'project.read' },
    ],
  },
  {
    titleKey: 'nav.sectionBilling',
    module: 'billing',
    items: [
      { to: '/quotations', labelKey: 'nav.quotations', icon: <FileText size={20} />, entity: 'quotations', permission: 'quotation.read' },
      { to: '/invoices', labelKey: 'nav.invoices', icon: <Receipt size={20} />, entity: 'invoices', permission: 'invoice.read' },
      // Return invoices, beside the invoices they reduce (DECISIONS.md §16).
      { to: '/credit-notes', labelKey: 'nav.creditNotes', icon: <ReceiptText size={20} />, entity: 'creditNotes', permission: 'creditNote.read' },
      // Money going out, beside the money coming in (DECISIONS.md §14).
      { to: '/expenses', labelKey: 'nav.expenses', icon: <Wallet size={20} />, entity: 'expenses', permission: 'expense.read' },
      { to: '/payables', labelKey: 'nav.payables', icon: <HandCoins size={20} />, entity: 'payables', permission: 'report.financial' },
    ],
  },
  {
    titleKey: 'nav.sectionPeople',
    module: 'people',
    items: [
      { to: '/staff', labelKey: 'nav.staff', icon: <IdCard size={20} />, entity: 'staff', permission: 'staff.read' },
      { to: '/leave', labelKey: 'nav.leave', icon: <CalendarOff size={20} />, entity: 'leave', permission: 'leave.read' },
      { to: '/timesheets', labelKey: 'nav.timesheets', icon: <Timer size={20} />, entity: 'timesheets', permission: 'timesheet.read' },
    ],
  },
  {
    titleKey: 'nav.sectionAdmin',
    module: 'administration',
    items: [
      // Only admin and accounting hold report.team, so sales never sees this.
      { to: '/reports', labelKey: 'nav.reports', icon: <BarChart3 size={20} />, entity: 'reports', permission: 'report.team' },
      { to: '/team', labelKey: 'nav.team', icon: <Users size={20} />, entity: 'team', permission: 'user.read' },
      // Who did what, when. Not the Activity log, which is logged calls and visits.
      { to: '/audit-trail', labelKey: 'nav.auditTrail', icon: <ScrollText size={20} />, entity: 'team', permission: 'auditLog.read' },
    ],
  },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation()
  const { user, signOut, setLanguage, allows } = useAuth()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const location = useLocation()

  const current = (i18n.language === 'ar' ? 'ar' : 'en') as Language

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: BRAND.navy, color: '#fff' }}>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        {/* Only the badge, cropped from the master artwork. The full logo sets
            the company name in near-black type below the badge, which is
            invisible on navy — so that line is set as real text instead, which
            also means it translates and stays sharp at any size. */}
        <Box
          component="img"
          src="/brand/logo-mark.png"
          alt="MEFOSA"
          width={480}
          height={153}
          sx={{ width: '100%', maxWidth: 168, height: 'auto', display: 'block' }}
        />
        <Typography
          variant="caption"
          component="p"
          sx={{ mt: 1.25, lineHeight: 1.4, fontWeight: 600, letterSpacing: '0.01em' }}
        >
          {t('app.company')}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75 }}>
          {t('app.tagline')}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {SECTIONS.map((section) => {
          // A section disappears when none of its screens is granted.
          if (user && !hasModule(user, section.module)) return null
          // BOTH: granted the screen, and a role that can actually use it.
          const visible = user
            ? section.items.filter(
              (i) => hasEntity(user, i.entity) && (!i.permission || allows(i.permission)),
            )
            : []
          if (visible.length === 0) return null
          return (
            <List
              key={section.titleKey}
              dense
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{ bgcolor: 'transparent', color: 'rgba(255,255,255,0.55)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}
                >
                  {t(section.titleKey)}
                </ListSubheader>
              }
            >
              {visible.map((item) => {
                const selected = location.pathname.startsWith(item.to)
                return (
                  <Box component="li" key={item.to} sx={{ listStyle: 'none' }}>
                  <ListItemButton
                    component={NavLink}
                    to={item.to}
                    selected={selected}
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      mx: 1,
                      borderRadius: 1.5,
                      color: 'inherit',
                      '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.16)' },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={t(item.labelKey)} slotProps={{ primary: { sx: { fontWeight: selected ? 700 : 500 } } }} />
                  </ListItemButton>
                  </Box>
                )
              })}
            </List>
          )
        })}
      </Box>
    </Box>
  )

  const rtl = theme.direction === 'rtl'

  return (
    // The drawer sits in normal flex flow on desktop rather than being
    // fixed-positioned with calculated offsets. Flex reverses on its own in
    // RTL, so the sidebar lands on the correct side in both directions with
    // no width arithmetic to get wrong.
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: 0, display: { md: 'flex' } }}
      >
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          anchor={rtl ? 'right' : 'left'}
          sx={{
            // The docked wrapper must stretch too, or the navy panel stops
            // short on pages taller than the viewport.
            '&.MuiDrawer-docked': { height: 'auto', display: 'flex' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              border: 'none',
              // In flow on desktop, overlaying on mobile.
              position: isDesktop ? 'sticky' : 'fixed',
              insetBlockStart: 0,
              height: '100dvh',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="sticky"
          color="inherit"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
          elevation={0}
        >
        <Toolbar sx={{ gap: 1 }}>
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label={t('nav.openMenu')}>
              <MenuIcon size={22} />
            </IconButton>
          )}
          <Box sx={{ flex: 1 }} />

          <Tooltip title={t('language.switch')}>
            <IconButton
              onClick={() => void setLanguage(current === 'en' ? 'ar' : 'en')}
              aria-label={t('language.switch')}
            >
              <Languages size={20} />
            </IconButton>
          </Tooltip>

          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={user?.fullName}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
              {user?.fullName?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{user?.fullName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t(`enums.role.${user?.role}`)}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => void setLanguage(current === 'en' ? 'ar' : 'en')}>
              <ListItemIcon><Languages size={18} /></ListItemIcon>
              {current === 'en' ? t('language.ar') : t('language.en')}
            </MenuItem>
            <MenuItem onClick={() => void signOut()}>
              <ListItemIcon><LogOut size={18} /></ListItemIcon>
              {t('auth.signOut')}
            </MenuItem>
          </Menu>
        </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{ flex: 1, minWidth: 0, px: { xs: 2, sm: 3 }, py: 3 }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}
